import Upload from "rc-upload";
import React, { ReactNode } from "react";
import { observer } from "mobx-react-lite";

import { langStore, translate, useSafeExecute, useSelecting } from "@noli/business";

import type { UploadMeta } from "@/types/meta";
import { SafeOp } from "@/utils/safeOp";
import { rectangleToImage, toast, useOwnToast } from "@/utils/misc";
import { Actions } from "@/utils/api";
import { Editor_Words } from "@/utils/lang/editor";

export interface UploaderProps {
  className?: string;
  children: ReactNode;
  onUpload?: (url: string) => void;
  addToBoard?: boolean;
}

const Uploader: React.FC<UploaderProps> = ({
  className,
  children,
  onUpload,
  addToBoard = true,
}) => {
  const t = useOwnToast();
  const lang = langStore.tgt;
  const node = useSelecting("raw").displayNode;

  return (
    <Upload
      className={className}
      accept=".png, .jpeg, .jpg, .webp"
      customRequest={async ({ file }) => {
        toast(t, "success", translate(Editor_Words["uploading-message"], lang));
        const blob = file as Blob;
        const uploadRes = await SafeOp.uploadImage(t, lang, blob, { failed, returnWH: true });
        if (!uploadRes) return;
        const { cdn } = uploadRes.urls;
        onUpload?.(cdn);
        if (!addToBoard) return;
        const meta: UploadMeta = {
          type: "upload",
          data: {
            url: cdn,
            wh: uploadRes.wh,
            isDrag: false,
          },
        };
        if (node?.type !== "rectangle") {
          Actions.importMeta({
            t,
            lang,
            type: "upload",
            meta,
            setupInfo: {
              checkBusy: true,
              busyAliases: [".upload."],
            },
            opt: null,
          });
        } else {
          const image = rectangleToImage(node, cdn);
          image.meta = meta;
          useSafeExecute("update", null, true, { success, failed })({
            alias: node.alias,
            json: image.toJson(),
          });
        }

        async function success(): Promise<void> {
          toast(t, "success", translate(Editor_Words["upload-seccess-message"], lang));
        }
        async function failed(): Promise<void> {
          toast(t, "error", translate(Editor_Words["upload-image-error-message"], lang));
        }
      }}
    >
      {children}
    </Upload>
  );
};

export default observer(Uploader);
